// ============================================================
// useDefenceResolution - Defence chain resolution hook.
//
// Supports both solo (local pendingAttack) and multiplayer
// (Firestore PendingDefence with transaction-based resolution).
//
// Defence chain: Dodge → Step Aside → Parry → Wall of Steel
// with each successful reaction removing 1+DoS hits.
//
// In multiplayer, the Firestore transaction fires on the FIRST
// step only. Subsequent steps resolve locally on the defending
// player's client.
// ============================================================
//
// The work lives in `./defence/`: chain (the chain's arithmetic),
// flow (what an answer does), claim (the server claim). This hook owns
// the two pieces of state and keeps each handler's identity stable
// across renders exactly as far as its inputs are.
// ============================================================
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import { useState, useCallback, useMemo, useRef } from 'react';
import type { DefenceStep, DefenceStepResult } from '../engine/defenceChain';
import type { Tab } from '../types';
import type { ConditionModOptions } from '../engine/conditionModifiers';
import { advanceChain as nextChain, isChainDone, rollChainStep } from './defence/chain';
import type { ChainRules } from './defence/chain';
import { chooseDefence, finishChain, passThrough, type ResolveSink } from './defence/flow';
import type { DefenceChainState, MultiplayerService, PendingAttackData } from './defence/types';

export interface UseDefenceResolutionParams {
  pendingAttack: PendingAttackData | null;
  setPendingAttack: (pa: PendingAttackData | null) => void;
  tabs: Tab[];
  applyHits: (tabId: string, data: unknown) => void;
  // Phase 7: Furious Assault after defence resolution
  setPendingBonusAttack?:
    | ((
        pba: { talentName: string; slot: unknown; tabId: string; modifiedSkill: number; actorId: string | null } | null,
      ) => void)
    | null;
  sessionId?: string | null;
  playerId?: string | null;
  clearPendingDefence?: ((tabId: string) => void) | null;
  mp?: MultiplayerService | null;
  /** Unnatural DoS house rule - 'off' (default) leaves reaction DoS untouched. */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Condition-modifier house rule - when true, a target's conditions gate/penalise its reactions. */
  applyConditionModifiers?: boolean;
  /** Which of those the table has switched off - see `ConditionModOptions`. */
  condModOptions?: ConditionModOptions;
  /**
   * A reaction was actually TAKEN (dodge or parry chosen, not 'none') -
   * RAW spends the reactor's aim. The component owns condition writes;
   * this hook only announces who reacted.
   */
  onReactionTaken?: ((tabId: string) => void) | null;
}

export interface UseDefenceResolutionReturn {
  handleDefenceChoice: (
    choice: string,
    multiplayerDefence?: PendingAttackData | null,
    manualMod?: number,
  ) => Promise<void>;
  handleDefenceStep: (step: DefenceStep) => void;
  acceptRemainingHits: () => void;
  defenceResolving: boolean;
  defenceChain: DefenceChainState | null;
}

/** The table's rulings, defaulted - one object, rebuilt only when a ruling changes. */
function useChainRules({
  unnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE,
  applyConditionModifiers = false,
  condModOptions,
}: UseDefenceResolutionParams): ChainRules {
  return useMemo(
    () => ({ unnaturalDosMode, applyConditionModifiers, condModOptions }),
    [unnaturalDosMode, applyConditionModifiers, condModOptions],
  );
}

/** The chain's own handlers: finalize, and advance by a rolled step. */
function useChainSteps(
  sink: ResolveSink,
  [defenceChain, setDefenceChain]: [DefenceChainState | null, (c: DefenceChainState | null) => void],
  rules: ChainRules,
) {
  const { unnaturalDosMode, applyConditionModifiers, condModOptions } = rules;
  /** Finalize the chain: applyHits with the remaining hits and the chain's results, then clean up. */
  const finalizeChain = useCallback((chain: DefenceChainState) => finishChain(sink, chain), [sink]);
  /** Advance the chain after a step; auto-complete on 0 hits or no available steps. */
  const advanceChain = useCallback(
    (chain: DefenceChainState, stepResult: DefenceStepResult) => {
      const next = nextChain(chain, stepResult, { applyConditionModifiers, condModOptions });
      if (isChainDone(next)) finalizeChain(next);
      else setDefenceChain(next);
    },
    // The two rulings advancing reads - never unnaturalDosMode, so a change to it keeps this handler.
    [finalizeChain, applyConditionModifiers, condModOptions, setDefenceChain],
  );
  /** A later chain step (Step Aside, Wall of Steel, or another dodge/parry within the chain). */
  const handleDefenceStep = useCallback(
    (step: DefenceStep) => {
      if (defenceChain) advanceChain(defenceChain, rollChainStep(defenceChain, step, unnaturalDosMode));
    },
    [defenceChain, advanceChain, unnaturalDosMode],
  );
  return { finalizeChain, handleDefenceStep };
}

/**
 * The initial choice: 'dodge' and 'parry' start the chain (in multiplayer
 * the Firestore claim fires here); 'none' passes all hits through.
 *
 * `onReactionTaken` is read through a ref that always holds the latest
 * callback: a dependency would re-create the handler whenever the component
 * re-creates the callback, and leaving it out (as it once was) told a stale
 * callback (v8.377.6).
 */
function useDefenceChoice(
  params: UseDefenceResolutionParams,
  sink: ResolveSink,
  rules: ChainRules,
  setDefenceResolving: (v: boolean) => void,
) {
  const { pendingAttack, tabs, playerId, mp, onReactionTaken = null } = params;
  const onReactionTakenRef = useRef(onReactionTaken);
  onReactionTakenRef.current = onReactionTaken;
  return useCallback(
    (choice: string, multiplayerDefence?: PendingAttackData | null, manualMod = 0) =>
      chooseDefence(
        {
          ...{ sink, pendingAttack, tabs, playerId, mp, setDefenceResolving, ...rules },
          onReactionTaken: onReactionTakenRef.current,
        },
        { choice, multiplayerDefence, manualMod },
      ),
    // setDefenceResolving is a useState setter: stable, so listing it changes nothing.
    [pendingAttack, tabs, playerId, mp, sink, rules, setDefenceResolving],
  );
}

export default function useDefenceResolution(params: UseDefenceResolutionParams): UseDefenceResolutionReturn {
  const { pendingAttack, setPendingAttack, applyHits, setPendingBonusAttack, sessionId, clearPendingDefence } = params;
  const rules = useChainRules(params);
  const [defenceResolving, setDefenceResolving] = useState(false);
  const [defenceChain, setDefenceChain] = useState<DefenceChainState | null>(null);
  /** Where a resolved defence goes - one object, rebuilt only when one of its parts changes. */
  const sink: ResolveSink = useMemo(
    () => ({ applyHits, sessionId, clearPendingDefence, setPendingAttack, setPendingBonusAttack, setDefenceChain }),
    [applyHits, sessionId, clearPendingDefence, setPendingAttack, setPendingBonusAttack],
  );
  const { finalizeChain, handleDefenceStep } = useChainSteps(sink, [defenceChain, setDefenceChain], rules);
  /** Accept the remaining hits at any point; with no chain, a pass for whatever prompt exists. */
  const acceptRemainingHits = useCallback(() => {
    if (defenceChain) finalizeChain(defenceChain);
    else if (pendingAttack) passThrough(sink, pendingAttack);
  }, [defenceChain, pendingAttack, sink, finalizeChain]);
  const handleDefenceChoice = useDefenceChoice(params, sink, rules, setDefenceResolving);
  return { handleDefenceChoice, handleDefenceStep, acceptRemainingHits, defenceResolving, defenceChain };
}
